import { FC, useCallback, useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { Button as LMFButton, TypeStyle, fromMd, untilMd } from '@syconium/little-miss-figgy'

import { VariantSelectionFormContext } from '../../../../app/_components/forms/VariantSelectionForm'
import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import {
  useEmbroidery,
  useEmbroideryDialog,
} from '../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { getEmbroideryTypeForEmbroideryForm } from '../../../../containers/EmbroideryContainer/utils/embroideryFormUtils'
import { intlPrettyCurrency } from '../../../../lib/utils'

import { CartEmbroideryAddToBag } from './CartEmbroideryAddToBag'
import { EmbroideryAddToBag } from './EmbroideryAddToBag'
import { EmbroideryForm } from './EmbroideryForm'
import { EmbroideryFormHeader } from './EmbroideryFormHeader'
import { EmbroideryOptionList } from './EmbroideryOptionList'
import { EmbroideryPriceSummary } from './EmbroideryPriceSummary'

export const EmbroideryOptionSelector: FC<{
  handleBackButtonClick: () => void
  isB2BColorSelected?: boolean
}> = ({ handleBackButtonClick, isB2BColorSelected }) => {
  const {
    clearEmbroiderySelections,
    withPricing,
    embroideryPriceRangeByType,
    selectedFormKind,
    hasSubmittedEmbroiderySelections,
    surface,
  } = useEmbroidery()
  const { confirmAndClose } = useEmbroideryDialog()

  const { locale, region } = useLocalization()
  const {
    embroidery: { modal: strings, embroideryFormKindTitles },
  } = useTranslation()

  // TODO: Remove when removing AddToBag Feature Flag
  const variantSelectionFormContext = useContext(VariantSelectionFormContext)
  const variantSelectionFormSelectedVariant = variantSelectionFormContext?.selectedVariant

  const headerPrice = useMemo(() => {
    const range = selectedFormKind
      ? embroideryPriceRangeByType[getEmbroideryTypeForEmbroideryForm(selectedFormKind)]
      : undefined

    if (!range || range.max.amount !== range.min.amount) return undefined

    return intlPrettyCurrency({
      unit: 'dollars',
      value: range.max.amount,
      region: region,
      locale: locale,
    })
  }, [embroideryPriceRangeByType, locale, region, selectedFormKind])

  const headerStep = useMemo(() => {
    switch (selectedFormKind) {
      case 'LOGO_SETUP':
        return '1/2'
      case 'LOGO_DTC':
        return '2/2'
      default:
        return null
    }
  }, [selectedFormKind])

  const formTitle = selectedFormKind ? embroideryFormKindTitles[`ADD_${selectedFormKind}`] : null
  const confirmDisabled = !hasSubmittedEmbroiderySelections

  const formHeaderTitleRef = useRef<HTMLHeadingElement>(null)
  const handleResetClick = useCallback(() => {
    clearEmbroiderySelections()
    if (formHeaderTitleRef.current) {
      formHeaderTitleRef.current.focus()
    }
  }, [clearEmbroiderySelections])

  const isCartSurface = surface === 'cart'
  const showEmbroideryATB = Boolean(
    (variantSelectionFormContext || isCartSurface) && !isB2BColorSelected
  )
  return (
    <Container>
      <EmbroideryFormHeader.Wrapper>
        <EmbroideryFormHeader.Column>
          {selectedFormKind ? (
            <EmbroideryFormHeader.BackButton
              text={strings.backButton}
              handleClick={handleBackButtonClick}
            />
          ) : null}
        </EmbroideryFormHeader.Column>
        <EmbroideryFormHeader.Column>
          {formTitle ? (
            <EmbroideryFormHeader.Title ref={formHeaderTitleRef}>
              {formTitle}
            </EmbroideryFormHeader.Title>
          ) : (
            <>
              <MenuTitle>{strings.previewTitle}</MenuTitle>
              <MenuSubtitle>{strings.previewSubtitle}</MenuSubtitle>
            </>
          )}
        </EmbroideryFormHeader.Column>
        <EmbroideryFormHeader.Column>
          {headerStep ? (
            <EmbroideryFormHeader.Step step={headerStep} />
          ) : headerPrice && withPricing ? (
            <EmbroideryFormHeader.Price price={headerPrice} />
          ) : null}
        </EmbroideryFormHeader.Column>
      </EmbroideryFormHeader.Wrapper>

      <>
        {selectedFormKind ? (
          <FormContent>
            <EmbroideryForm formKind={selectedFormKind} />
          </FormContent>
        ) : (
          <Content>
            <EmbroideryOptions>
              <EmbroideryOptionList />
            </EmbroideryOptions>
            <BottomContent>
              <ButtonWrap>
                {showEmbroideryATB ? (
                  <>
                    {withPricing && !isCartSurface && variantSelectionFormSelectedVariant && (
                      <EmbroideryPriceSummary
                        selectedVariantPrice={variantSelectionFormSelectedVariant.price}
                      />
                    )}
                    {isCartSurface ? <CartEmbroideryAddToBag /> : <EmbroideryAddToBag />}
                  </>
                ) : (
                  <Button
                    data-testid='embroidery-action-button'
                    aria-label={strings.confirm}
                    onClick={confirmDisabled ? undefined : confirmAndClose}
                    aria-disabled={confirmDisabled}
                  >
                    {strings.confirm}
                  </Button>
                )}
                {!confirmDisabled && (
                  <ResetButton
                    aria-label={strings.forms.submissionButtons.resetCta}
                    variant='black-on-white'
                    onClick={handleResetClick}
                  >
                    {strings.forms.submissionButtons.resetCta}
                  </ResetButton>
                )}
              </ButtonWrap>
            </BottomContent>
          </Content>
        )}
      </>
    </Container>
  )
}

const Container = styled.div`
  background-color: ${o => o.theme.color.fill.background};
  overflow-y: auto;
  padding: 0;
  margin: 0;
  flex: 1;

  ${untilMd} {
    max-height: 100%;
    padding-bottom: ${o => o.theme.spacing(6)};
  }
  ${fromMd} {
    border: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.subtle};
    width: 420px;
  }
`

const MenuTitle = styled.h2`
  ${TypeStyle.css.headlineExtraSmall}
  text-transform: capitalize;
  text-align: center;
`

const MenuSubtitle = styled.h3`
  ${TypeStyle.css.headlineMicro}
  text-align: center;
  font-weight: 400;
  text-transform: none;
  font-size: 14px;
  margin-top: ${o => o.theme.spacing(2)};
`

const Content = styled.div`
  padding: 0 ${o => o.theme.spacing(4)};
  ${fromMd} {
    display: flex;
    flex-direction: column;
    height: calc(100% - ${o => o.theme.spacing(37)});
    justify-content: space-between;
    flex-grow: 1;
    padding: ${o => o.theme.spacing(0, 10, 0, 10)};
  }
`

const FormContent = styled(Content)`
  ${untilMd} {
    padding: 0;
  }
`
const EmbroideryOptions = styled.div``

const BottomContent = styled.div``
const ButtonWrap = styled.div`
  margin-bottom: ${o => o.theme.spacing(10)};
`

const Button = styled(LMFButton)`
  width: 100%;
  padding: ${o => o.theme.spacing(4)} ${o => o.theme.spacing(8)};

  &[aria-disabled='true'] {
    opacity: 0.7;
    background-color: ${o => o.theme.color.deprecated.stoneLight};
    border-color: ${o => o.theme.color.deprecated.stoneLight};
    color: ${o => o.theme.color.deprecated.obsidianLight};
  }
`

const ResetButton = styled(Button)`
  margin-top: ${o => o.theme.spacing(2)};
`
